import { useMemo, useCallback } from "react";
import type { ValidateListType } from "@hooks/useRosterValidation.ts";

export type UseRosterViolationFilterParams = {
  rawValidateList?: ValidateListType | null;
  showManpowerViolations: boolean;
  showConfigViolations: boolean;
};

export type UseRosterViolationFilterResult = {
  validateList: ValidateListType | null;
  filteredViolationCount: number;
  hasManpowerViolationByShift: (shiftCode?: string | null) => boolean;
};

/**
 * 위반사항 토글 상태에 따라 rawValidateList를 필터링하고,
 * 건수 및 시프트별 최소인원 위반 여부 조회 함수를 반환한다.
 */
export function useRosterViolationFilter({
  rawValidateList,
  showManpowerViolations,
  showConfigViolations,
}: UseRosterViolationFilterParams): UseRosterViolationFilterResult {
  // 토글 상태에 따라 위반사항 필터링
  const validateList = useMemo(() => {
    if (!rawValidateList?.violations || !rawValidateList?.detailed_violations) {
      return rawValidateList ?? null;
    }

    const filteredViolations: string[] = [];
    const filteredDetailedViolations: typeof rawValidateList.detailed_violations =
      [];

    // violations와 detailed_violations는 인덱스가 일치한다고 가정
    rawValidateList.violations.forEach((violation, index) => {
      const detail = rawValidateList.detailed_violations[index];
      if (!detail) return;

      // 최소인원 위반 필터링
      if (
        detail.type === "manpower_shortage" ||
        detail.type === "manpower_excess"
      ) {
        if (showManpowerViolations) {
          filteredViolations.push(violation);
          filteredDetailedViolations.push(detail);
        }
        return;
      }

      // 설정 위반 필터링
      if (showConfigViolations) {
        filteredViolations.push(violation);
        filteredDetailedViolations.push(detail);
      }
    });

    return {
      violations: filteredViolations,
      detailed_violations: filteredDetailedViolations,
    };
  }, [rawValidateList, showManpowerViolations, showConfigViolations]);

  // 필터링된 위반사항 건수 계산
  const filteredViolationCount = useMemo(() => {
    if (!validateList?.violations) return 0;
    return validateList.violations.length;
  }, [validateList]);

  const hasManpowerViolationByShift = useCallback(
    (shiftCode?: string | null) => {
      if (!shiftCode) return false;
      const details = validateList?.detailed_violations;
      if (!details) return false;
      return details.some(
        (v) =>
          (v?.type === "manpower_shortage" ||
            v?.type === "manpower_excess") &&
          v?.shift === shiftCode
      );
    },
    [validateList?.detailed_violations]
  );

  return {
    validateList,
    filteredViolationCount,
    hasManpowerViolationByShift,
  };
}
